import { useMemo } from 'react'
import GcpCollapsible from '@/components/gcp/GcpCollapsible'
import GcpCopyButton from '@/components/gcp/GcpCopyButton'
import GcpRuleBasedCallout from '@/components/gcp/GcpRuleBasedCallout'
import GcpSectionErrorBanner from '@/components/gcp/GcpSectionErrorBanner'
import GcpStatCard from '@/components/gcp/GcpStatCard'
import {
  asNumber,
  asRecordArray,
  asString,
  filterCloudRunBillingModeNewSkus,
  formatSqlConnectionPath,
  formatMonthLabel,
  formatPct,
  formatUsd,
  getSectionData,
  isRecord,
  type RuleBasedRecommendation,
} from '@/components/gcp/gcpHelpers'
import type {
  CloudRunBillingModeByService,
  CloudRunBillingModeChange,
  GcpAnalysisResult,
  GcpAnalyzerSectionKey,
  NewSkuRow,
} from '@/services/gcpCosts'
import { isGcpSectionError } from '@/services/gcpCosts'

interface GcpServiceSectionProps {
  analysis: GcpAnalysisResult
  sectionKey: Extract<GcpAnalyzerSectionKey, 'cloud_run' | 'cloud_sql' | 'gcs' | 'compute'>
  title: string
  description: string
}

function SimpleTable({
  columns,
  rows,
}: {
  columns: Array<{ key: string; label: string; align?: 'left' | 'right' }>
  rows: Record<string, unknown>[]
}) {
  if (rows.length === 0) return <p className="text-sm text-gray-500">No data.</p>
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={`px-3 py-2 font-medium text-gray-600 ${col.align === 'right' ? 'text-right' : 'text-left'}`}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row, index) => (
            <tr key={index}>
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`px-3 py-2 text-gray-800 ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                >
                  {formatValue(row[col.key], col.key)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function formatValue(value: unknown, key: string): string {
  if (value == null) return '—'
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '—'
    if (key.includes('cost') || key.includes('usd') || key.includes('amount')) {
      return formatUsd(value)
    }
    return value.toLocaleString('en-US', { maximumFractionDigits: 4 })
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'string') return value
  return JSON.stringify(value)
}

function sumCosts(costRows: Record<string, unknown>[]): number {
  return costRows.reduce((sum, row) => sum + (asNumber(row.net_cost) ?? 0), 0)
}

export default function GcpServiceSection({
  analysis,
  sectionKey,
  title,
  description,
}: GcpServiceSectionProps) {
  const section = analysis.sections[sectionKey]
  const data = getSectionData(analysis, sectionKey)

  const recommendations = useMemo(
    () => (data ? (asRecordArray(data.recommendations) as RuleBasedRecommendation[]) : []),
    [data]
  )

  if (isGcpSectionError(section)) {
    return (
      <GcpSectionErrorBanner
        title={`${title} analysis failed`}
        error={section.error}
        missingRoleHint={section.missing_role_hint}
      />
    )
  }

  if (!data) return null

  const costRows = asRecordArray(data.costs)
  const totalCost = sumCosts(costRows)
  const months = Math.max(analysis._meta.analysis_months, 1)
  const estMonthly = totalCost / months

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">{description}</p>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <GcpStatCard label="Est. monthly cost" value={formatUsd(estMonthly)} />
        {sectionKey === 'cloud_run' ? (
          <GcpStatCard
            label="Services"
            value={asRecordArray(data.services).length}
            subtext="Cloud Run services in project"
          />
        ) : null}
        {sectionKey === 'cloud_sql' ? (
          <GcpStatCard
            label="Instances"
            value={asRecordArray(data.instances).length}
            subtext="Cloud SQL instances"
          />
        ) : null}
        {sectionKey === 'gcs' ? (
          <GcpStatCard
            label="Buckets"
            value={asRecordArray(data.buckets).length}
            subtext="GCS buckets in project"
          />
        ) : null}
        {sectionKey === 'compute' ? (
          <GcpStatCard
            label="Cleanup candidates"
            value={
              asRecordArray(data.unused_static_ips).length +
              asRecordArray(data.unattached_disks).length +
              asRecordArray(data.old_snapshots).length
            }
            subtext="Unused IPs, disks, old snapshots"
          />
        ) : null}
      </div>

      {recommendations.map((rec, index) => (
        <GcpRuleBasedCallout key={`${rec.type ?? 'rec'}-${index}`} recommendation={rec} />
      ))}

      {sectionKey === 'cloud_run' ? (
        <CloudRunContent analysis={analysis} data={data} />
      ) : null}
      {sectionKey === 'cloud_sql' ? <CloudSqlContent data={data} /> : null}
      {sectionKey === 'gcs' ? <GcsContent data={data} /> : null}
      {sectionKey === 'compute' ? <ComputeContent data={data} /> : null}
    </div>
  )
}

function CloudRunContent({
  analysis,
  data,
}: {
  analysis: GcpAnalysisResult
  data: Record<string, unknown>
}) {
  const services = asRecordArray(data.services)
  const billingModeByService = parseBillingModeByService(data.billing_mode_by_service)
  const billingModeChanges = parseBillingModeChanges(data.billing_mode_changes)
  const billingSection = getSectionData(analysis, 'billing')
  const billingNewSkus = parseBillingNewSkus(billingSection)

  return (
    <>
      {billingModeByService.length > 0 ? (
        <div>
          <h3 className="mb-2 text-sm font-medium text-gray-800">Billing mode by service</h3>
          <SimpleTable
            columns={[
              { key: 'service', label: 'Service' },
              { key: 'instance_based', label: 'Instance-based', align: 'right' },
              { key: 'min_instance', label: 'Min instance', align: 'right' },
              { key: 'request_based', label: 'Request-based', align: 'right' },
              { key: 'jobs', label: 'Jobs', align: 'right' },
              { key: 'avg_cpu_utilization_pct', label: 'Avg CPU', align: 'right' },
            ]}
            rows={billingModeByService.map((row) => ({
              service: row.service,
              instance_based: row.modes.instance_based,
              min_instance: row.modes.min_instance,
              request_based: row.modes.request_based,
              jobs: row.modes.jobs,
              avg_cpu_utilization_pct:
                row.avg_cpu_utilization_pct != null
                  ? formatPct(row.avg_cpu_utilization_pct)
                  : '—',
            }))}
          />
          {billingModeChanges.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-2">
              {billingModeChanges.map((change) => (
                <span
                  key={`${change.service}-${change.sku}`}
                  className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800"
                >
                  {change.service}: billing mode changed in{' '}
                  {formatMonthLabel(change.first_seen_month)} ({formatUsd(change.monthly_cost_usd)}
                  /mo)
                </span>
              ))}
            </div>
          ) : null}
          {filterCloudRunBillingModeNewSkus(billingNewSkus).length > 0 ? (
            <p className="mt-2 text-xs text-gray-500">
              Also flagged in billing “What changed”:{' '}
              {filterCloudRunBillingModeNewSkus(billingNewSkus)
                .map((sku) => `${sku.sku} (${formatMonthLabel(sku.first_seen_month)})`)
                .join('; ')}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="grid gap-3 md:grid-cols-2">
        {services.map((svc) => {
          const metrics = isRecord(svc.metrics) ? svc.metrics : {}
          const cpuUtil = isRecord(svc.cpu_utilization) ? svc.cpu_utilization : {}
          const memUtil = isRecord(svc.memory_utilization) ? svc.memory_utilization : {}
          return (
            <div key={asString(svc.name)} className="rounded-md border border-gray-200 p-3">
              <p className="font-medium text-gray-900">{asString(svc.name)}</p>
              <p className="mt-1 text-xs text-gray-500">
                {asString(svc.location)} · CPU {asString(svc.cpu)} · Memory {asString(svc.memory)}
              </p>
              <p className="mt-2 text-sm text-gray-700">
                Min/max instances: {asNumber(svc.min_instances) ?? 0} /{' '}
                {asNumber(svc.max_instances) ?? '—'} · CPU idle:{' '}
                {svc.cpu_idle === true ? 'yes' : svc.cpu_idle === false ? 'no' : '—'}
              </p>
              <p className="mt-1 text-sm text-gray-700">
                Requests/day: {asNumber(metrics.avg_requests_per_day)?.toFixed(0) ?? '—'} · CPU
                util: {formatPct(asNumber(cpuUtil.avg_cpu_utilization))} · Memory util:{' '}
                {formatPct(asNumber(memUtil.avg_memory_utilization))}
              </p>
            </div>
          )
        })}
      </div>
      <GcpCollapsible title="Raw Cloud Run cost data" defaultOpen={false}>
        <SimpleTable
          columns={[
            { key: 'month', label: 'Month' },
            { key: 'resource_name', label: 'Resource' },
            { key: 'sku', label: 'SKU' },
            { key: 'net_cost', label: 'Net cost', align: 'right' },
          ]}
          rows={asRecordArray(data.costs)}
        />
      </GcpCollapsible>
    </>
  )
}

function CloudSqlContent({ data }: { data: Record<string, unknown> }) {
  const instances = asRecordArray(data.instances)
  const showIpColumns = instances.some((inst) => inst.ipv4_enabled !== undefined)

  return (
    <>
      {showIpColumns ? (
        <div>
          <h3 className="mb-2 text-sm font-medium text-gray-800">Instance connection paths</h3>
          <SimpleTable
            columns={[
              { key: 'name', label: 'Instance' },
              { key: 'connection_path', label: 'IP path' },
              { key: 'ipv4_enabled', label: 'Public IPv4' },
              { key: 'authorized_networks_count', label: 'Auth networks', align: 'right' },
            ]}
            rows={instances.map((inst) => ({
              name: inst.name,
              connection_path: formatSqlConnectionPath(
                inst.ipv4_enabled === true,
                typeof inst.private_network === 'string' ? inst.private_network : null
              ),
              ipv4_enabled: inst.ipv4_enabled,
              authorized_networks_count: inst.authorized_networks_count,
            }))}
          />
        </div>
      ) : null}

      <div className="grid gap-3 md:grid-cols-2">
        {instances.map((inst) => {
          const cpuUtil = isRecord(inst.cpu_utilization) ? inst.cpu_utilization : {}
          const memUtil = isRecord(inst.memory_utilization) ? inst.memory_utilization : {}
          const diskUtil = isRecord(inst.disk_utilization) ? inst.disk_utilization : {}
          const connections = isRecord(inst.connections) ? inst.connections : {}
          return (
            <div key={asString(inst.name)} className="rounded-md border border-gray-200 p-3">
              <p className="font-medium text-gray-900">{asString(inst.name)}</p>
              <p className="mt-1 text-xs text-gray-500">
                {asString(inst.tier)} · {asString(inst.availability_type)} ·{' '}
                {asNumber(inst.data_disk_size_gb) ?? 0}GB disk
              </p>
              <p className="mt-2 text-sm text-gray-700">
                CPU: {formatPct(asNumber(cpuUtil.avg_cpu_utilization))} (max{' '}
                {formatPct(asNumber(cpuUtil.max_cpu_utilization))}) · Memory:{' '}
                {formatPct(asNumber(memUtil.avg_memory_utilization))} · Disk:{' '}
                {formatPct(asNumber(diskUtil.avg_disk_utilization))}
              </p>
              {connections.avg_connections != null || connections.max_connections != null ? (
                <p className="mt-1 text-sm text-gray-700">
                  Connections: avg {asNumber(connections.avg_connections)?.toFixed(1) ?? '—'} · max{' '}
                  {asNumber(connections.max_connections)?.toFixed(0) ?? '—'}
                </p>
              ) : null}
              <p className="mt-1 text-sm text-gray-700">
                Backups: {inst.backup_enabled === true ? 'enabled' : 'disabled'}
              </p>
            </div>
          )
        })}
      </div>
      <GcpCollapsible title="Raw Cloud SQL cost data" defaultOpen={false}>
        <SimpleTable
          columns={[
            { key: 'month', label: 'Month' },
            { key: 'resource_name', label: 'Instance' },
            { key: 'sku', label: 'SKU' },
            { key: 'net_cost', label: 'Net cost', align: 'right' },
          ]}
          rows={asRecordArray(data.costs)}
        />
      </GcpCollapsible>
    </>
  )
}

function parseBillingModeByService(value: unknown): CloudRunBillingModeByService[] {
  return asRecordArray(value).map((row) => {
    const modesRaw = isRecord(row.modes) ? row.modes : {}
    return {
      service: asString(row.service),
      modes: {
        instance_based: asNumber(modesRaw.instance_based) ?? 0,
        min_instance: asNumber(modesRaw.min_instance) ?? 0,
        request_based: asNumber(modesRaw.request_based) ?? 0,
        jobs: asNumber(modesRaw.jobs) ?? 0,
        other: asNumber(modesRaw.other) ?? 0,
      },
      avg_cpu_utilization_pct: asNumber(row.avg_cpu_utilization_pct),
      cold_start_latency_ms: asNumber(row.cold_start_latency_ms),
    }
  })
}

function parseBillingModeChanges(value: unknown): CloudRunBillingModeChange[] {
  return asRecordArray(value).map((row) => ({
    service: asString(row.service),
    first_seen_month: asString(row.first_seen_month),
    sku: asString(row.sku),
    monthly_cost_usd: asNumber(row.monthly_cost_usd) ?? 0,
  }))
}

function parseBillingNewSkus(section: Record<string, unknown> | null): NewSkuRow[] {
  const raw = section?.what_changed
  if (!isRecord(raw)) return []
  return asRecordArray(raw.new_skus).map((row) => ({
    service: asString(row.service),
    sku: asString(row.sku),
    first_seen_month: asString(row.first_seen_month),
    monthly_cost_usd: asNumber(row.monthly_cost_usd) ?? 0,
  }))
}

function GcsContent({ data }: { data: Record<string, unknown> }) {
  const buckets = asRecordArray(data.buckets)
  const accessSummary = asRecordArray(data.access_summary)
  return (
    <>
      <div className="grid gap-3 md:grid-cols-2">
        {buckets.map((bucket) => (
          <div key={asString(bucket.name)} className="rounded-md border border-gray-200 p-3">
            <p className="font-medium text-gray-900">{asString(bucket.name)}</p>
            <p className="mt-1 text-xs text-gray-500">
              {asString(bucket.storage_class)} · {asString(bucket.location)}
            </p>
            <p className="mt-2 text-sm text-gray-700">
              Versioning: {bucket.versioning_enabled === true ? 'on' : 'off'} · Lifecycle rules:{' '}
              {asNumber(bucket.lifecycle_rules) ?? 0}
            </p>
          </div>
        ))}
      </div>
      {accessSummary.length > 0 ? (
        <GcpCollapsible title="Access patterns by bucket" defaultOpen={false}>
          <SimpleTable
            columns={[
              { key: 'bucket_name', label: 'Bucket' },
              { key: 'storage_cost', label: 'Storage $', align: 'right' },
              { key: 'operations_cost', label: 'Ops $', align: 'right' },
              { key: 'egress_cost', label: 'Egress $', align: 'right' },
              { key: 'egress_pct_of_cost', label: 'Egress %', align: 'right' },
            ]}
            rows={accessSummary}
          />
        </GcpCollapsible>
      ) : null}
      <GcpCollapsible title="Raw GCS cost data" defaultOpen={false}>
        <SimpleTable
          columns={[
            { key: 'month', label: 'Month' },
            { key: 'sku', label: 'SKU' },
            { key: 'net_cost', label: 'Net cost', align: 'right' },
          ]}
          rows={asRecordArray(data.costs)}
        />
      </GcpCollapsible>
    </>
  )
}

function ComputeContent({ data }: { data: Record<string, unknown> }) {
  const unusedIps = asRecordArray(data.unused_static_ips)
  const unattachedDisks = asRecordArray(data.unattached_disks)
  const oldSnapshots = asRecordArray(data.old_snapshots)

  return (
    <>
      {unusedIps.length > 0 ? (
        <div>
          <h3 className="mb-2 text-sm font-medium text-gray-800">Unused static IPs</h3>
          <div className="space-y-2">
            {unusedIps.map((ip) => {
              const cmd = `gcloud compute addresses delete ${asString(ip.name)} --region=${asString(ip.region)}`
              return (
                <div
                  key={asString(ip.name)}
                  className="flex flex-col gap-2 rounded-md border border-gray-200 p-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="text-sm text-gray-800">
                    <span className="font-medium">{asString(ip.name)}</span> · {asString(ip.address)}{' '}
                    · {asString(ip.region)}
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="rounded bg-gray-100 px-2 py-1 text-xs">{cmd}</code>
                    <GcpCopyButton text={cmd} label="Copy" />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ) : null}

      {unattachedDisks.length > 0 ? (
        <div>
          <h3 className="mb-2 text-sm font-medium text-gray-800">Unattached disks</h3>
          <div className="space-y-2">
            {unattachedDisks.map((disk) => {
              const cmd = `gcloud compute disks delete ${asString(disk.name)} --zone=${asString(disk.zone)}`
              return (
                <div
                  key={asString(disk.name)}
                  className="flex flex-col gap-2 rounded-md border border-gray-200 p-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="text-sm text-gray-800">
                    <span className="font-medium">{asString(disk.name)}</span> ·{' '}
                    {asNumber(disk.size_gb) ?? 0}GB · {asString(disk.zone)}
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="rounded bg-gray-100 px-2 py-1 text-xs">{cmd}</code>
                    <GcpCopyButton text={cmd} label="Copy" />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ) : null}

      {oldSnapshots.length > 0 ? (
        <div>
          <h3 className="mb-2 text-sm font-medium text-gray-800">Snapshots older than 90 days</h3>
          <div className="space-y-2">
            {oldSnapshots.map((snap) => {
              const cmd = `gcloud compute snapshots delete ${asString(snap.name)}`
              return (
                <div
                  key={asString(snap.name)}
                  className="flex flex-col gap-2 rounded-md border border-gray-200 p-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="text-sm text-gray-800">
                    <span className="font-medium">{asString(snap.name)}</span> ·{' '}
                    {asNumber(snap.size_gb) ?? 0}GB · {asNumber(snap.days_old) ?? 0} days old
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="rounded bg-gray-100 px-2 py-1 text-xs">{cmd}</code>
                    <GcpCopyButton text={cmd} label="Copy" />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ) : null}

      <GcpCollapsible title="Compute & network cost breakdown" defaultOpen={false}>
        <SimpleTable
          columns={[
            { key: 'service', label: 'Service' },
            { key: 'sku', label: 'SKU' },
            { key: 'resource_name', label: 'Resource' },
            { key: 'net_cost', label: 'Net cost', align: 'right' },
          ]}
          rows={asRecordArray(data.costs)}
        />
      </GcpCollapsible>
    </>
  )
}
